import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Printer, Heart, Sparkle, ShieldCheck, ArrowRight } from "@phosphor-icons/react";
import api from "../lib/api";
import ListenButton from "../components/ListenButton";

const IMAGES = {
  hero: "/purple-stretch.webp",
  break1: "/shc-meal-2.webp",
  break2: "/shc-companionship-2.webp",
  break3: "/brand-transport.webp",
};

const TEAM = [
  { key: "kelly", name: "Kelly Schmunk", role: "Administrator", phone: "267-547-0310", initials: "KS" },
  { key: "patti", name: "Patti Carvahlo", role: "Staffing Manager", phone: "954-520-2843", initials: "PC" },
  { key: "bruce", name: "Bruce", role: "Silver Home Care", phone: "", initials: "B" },
];

const SECTION_ORDER = [
  ["company overview", "Company Overview"],
  ["our mission", "Our Mission & Service Philosophy"],
  ["description of services", "Services We Provide"],
  ["care plan development", "Your Care Plan"],
  ["direct care worker selection", "How We Choose & Train Your Caregivers"],
  ["client rights and responsibilities", "Your Rights & Responsibilities"],
  ["protection from abuse", "Protection from Abuse, Neglect & Exploitation"],
  ["communication support", "Communication & Language Access"],
  ["understanding and paying", "Paying for Home Care"],
  ["billing and payment", "Billing & Payment Policies"],
  ["client safety and emergencies", "Safety & Emergencies"],
  ["complaints and grievances", "Complaints & Grievances"],
  ["home surveillance", "Home Surveillance Guidelines"],
  ["scheduling and communication", "Scheduling & Communication"],
  ["financial boundaries", "Financial Boundaries & Medicaid Fraud Prevention"],
  ["advance directives", "Advance Directives & Future Planning"],
  ["incident notification", "Incident Notification"],
  ["privacy", "Your Privacy & Information Sharing"],
  ["termination of services", "Termination of Services"],
];

const IMAGE_BREAKS = { 4: IMAGES.break1, 9: IMAGES.break2, 14: IMAGES.break3 };

const SECTION_IMAGES = {
  "Company Overview": "/shc-door-greet.webp",
  "Our Mission & Service Philosophy": "/purple-comfort.webp",
  "Services We Provide": "/purple-serving.webp",
  "Your Care Plan": "/brand-personal-care.webp",
  "How We Choose & Train Your Caregivers": "/shc-gen-training-room.webp",
  "Your Rights & Responsibilities": "/purple-hair.webp",
  "Protection from Abuse, Neglect & Exploitation": "/purple-walker.webp",
  "Communication & Language Access": "/scene-videocall.webp",
  "Paying for Home Care": "/brand-tea.webp",
  "Billing & Payment Policies": "/orient-handbook.webp",
  "Safety & Emergencies": "/shc-gen-steps.webp",
  "Complaints & Grievances": "/brand-tea.webp",
  "Home Surveillance Guidelines": "/shc-door.webp",
  "Scheduling & Communication": "/shc-evv-phone.webp",
  "Financial Boundaries & Medicaid Fraud Prevention": "/orient-handbook.webp",
  "Advance Directives & Future Planning": "/purple-comfort.webp",
  "Incident Notification": "/brand-ambulation.webp",
  "Your Privacy & Information Sharing": "/purple-hair.webp",
  "Termination of Services": "/shc-gen-winter.webp",
};

const SECTION_FALLBACK = ["/purple-serving.webp", "/purple-walker.webp", "/shc-meal-2.webp", "/shc-gen-winter.webp",
  "/purple-stretch.webp", "/shc-curlers.webp", "/shc-gen-exercise.webp", "/shc-gen-pet.webp", "/shc-gen-garden.webp",
  "/scene-manicure.webp", "/shc-companionship-2.webp", "/brand-daily-care.webp", "/shc-gen-shower.webp", "/shc-door-greet.webp"];

// The five service categories in Section 8 are headings, not list items — name them explicitly so formatting is deterministic
const SERVICE_SUBHEADS = new Set([
  "Companionship",
  "Personal Care (ADLs – Activities of Daily Living)",
  "Instrumental Activities of Daily Living (IADLs)",
  "Medication Reminders",
  "Respite Care",
]);

function parseRawSections(content) {
  const out = [];
  let cur = null;
  for (const raw of (content || "").split("\n")) {
    const line = raw.trim();
    const m = line.match(/^(\d{1,2})\.\s+([A-Z].+)$/);
    if (m && Number(m[1]) >= 2 && Number(m[1]) <= 21) {
      if (cur) out.push(cur);
      cur = { title: m[2].trim(), body: [] };
    } else if (cur) {
      cur.body.push(raw);
    }
  }
  if (cur) out.push(cur);
  return out;
}

function buildSections(content) {
  const raw = parseRawSections(content);
  const sections = [];
  for (const [key, display] of SECTION_ORDER) {
    const matches = raw.filter((s) => s.title.toLowerCase().includes(key));
    if (!matches.length) continue;
    const body = matches.map((m) => m.body.join("\n")).sort((a, b) => b.length - a.length)[0];
    sections.push({ id: `section-${sections.length + 1}`, title: display, body });
  }
  return sections;
}

function renderBody(body, testid) {
  const out = [];
  let list = [];
  let pi = 0;
  const flushList = () => {
    if (list.length) {
      const items = list;
      list = [];
      out.push(
        <ul key={`ul${out.length}`} className="mb-5 list-disc space-y-2 pl-6 text-base leading-[1.8] text-[#45403B] marker:text-[#A8A3A0] md:text-lg" data-testid={`${testid}-list-${out.length}`}>
          {items.map((t, j) => <li key={j}>{t}</li>)}
        </ul>
      );
    }
  };
  const lines = body.split("\n").map((l) => l.trim());
  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    if (!line) { flushList(); continue; }
    if (/^\d{1,3}$/.test(line)) continue;
    if (/^\d+\.\d+\s+\S/.test(line)) {
      flushList();
      out.push(<h3 key={`h${pi++}`} className="mb-2.5 mt-8 font-heading text-lg font-semibold text-[#3A343C] md:text-xl">{line}</h3>);
      continue;
    }
    if (/^[A-Z0-9 &'\-–—|:®©.]{8,}$/.test(line) && /[A-Z]/.test(line) && !/[a-z]/.test(line)) {
      flushList();
      out.push(<h3 key={`h${pi++}`} className="mb-2.5 mt-8 font-heading text-lg font-semibold uppercase tracking-wide text-[#3A343C] md:text-xl">{line}</h3>);
      continue;
    }
    if (/^[•\-*]\s+/.test(line)) {
      list.push(line.replace(/^[•\-*]\s+/, ""));
      continue;
    }
    // lead-in line ending with a colon → bold intro, following short lines become bullets
    if (/:\s*$/.test(line) && line.length < 120) {
      flushList();
      out.push(<p key={`p${pi++}`} className="mb-2 mt-5 text-base font-semibold text-[#3A343C] md:text-lg" data-testid={`${testid}-lead-${pi}`}>{line}</p>);
      continue;
    }
    // service-category subheading: explicit names, or short standalone line followed by long prose and not inside a bullet run
    const nextLine = lines.slice(li + 1).find((l) => l.trim()) || "";
    const inListRun = list.length > 0;
    if (SERVICE_SUBHEADS.has(line) || (!inListRun && line.length < 60 && !/[.!?:]$/.test(line) && nextLine.trim().length > 70)) {
      flushList();
      out.push(<h4 key={`h${pi++}`} className="mb-2 mt-6 font-heading text-base font-semibold text-[#3A343C] md:text-lg" data-testid={`${testid}-sub-${pi}`}>{line}</h4>);
      continue;
    }
    // short list-like line (no terminal punctuation, brief) or short run after a lead-in → bullet
    const prevWasLead = pi > 0 && out.length && out[out.length - 1]?.props?.["data-testid"]?.includes("-lead-");
    if ((line.length < 90 && !/[.!?]$/.test(line)) || (prevWasLead && line.length < 160)) {
      list.push(line.replace(/^\d+\.\s+/, ""));
      continue;
    }
    flushList();
    out.push(<p key={`p${pi++}`} className="mb-4 text-base leading-[1.8] text-[#45403B] md:text-lg" data-testid={`${testid}-para-${pi}`}>{line}</p>);
  }
  flushList();
  return out;
}

function Monogram({ initials, size = "h-20 w-20 text-2xl" }) {
  return (
    <span className={`flex ${size} items-center justify-center rounded-full bg-[#E2DFE6] font-heading font-semibold text-[#82778A]`}>
      {initials}
    </span>
  );
}

export default function ClientHandbookPublicPage() {
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [active, setActive] = useState("");
  const articleRef = useRef(null);

  useEffect(() => {
    api.get("/public/client-handbook")
      .then((r) => setData(r.data))
      .catch(() => setMissing(true));
  }, []);

  const sections = useMemo(() => (data ? buildSections(data.content) : []), [data]);

  useEffect(() => {
    if (!sections.length) return;
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) setActive(e.target.id);
    }, { rootMargin: "-20% 0px -70% 0px" });
    sections.forEach((s) => { const el = document.getElementById(s.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [sections]);

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="handbook-public-page">
      <style>{`
        @media print {
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          header, footer, nav, .no-print { display: none !important; }
          main { max-width: 100% !important; }
          body { background: white !important; }
          section { break-inside: avoid-page; }
          img { max-width: 100% !important; }
        }
        html { scroll-behavior: smooth; }
        ::-webkit-scrollbar { width: 10px; }
        ::-webkit-scrollbar-track { background: #F2EFEA; }
        ::-webkit-scrollbar-thumb { background: #A8A3A0; border-radius: 8px; }
      `}</style>

      <header className="sticky top-0 z-50 border-b border-stone-200/60 bg-white/70 backdrop-blur-xl no-print">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8">
          <div className="flex items-center gap-3">
            <img src="/shc-logo.png" alt="Silver Home Care" className="h-10 w-auto" />
            <div>
              <p className="font-heading text-sm font-semibold text-[#4A424E]">Silver Home Care</p>
              <p className="text-[11px] text-[#5C5856]">Client Handbook</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} className="hidden items-center gap-1.5 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-medium text-[#5C5856] transition-colors hover:bg-stone-900 hover:text-white sm:inline-flex" data-testid="handbook-print">
              <Printer size={13} /> Print
            </button>
            <a href="tel:8007196912" className="hidden items-center gap-1.5 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-medium text-[#5C5856] transition-colors hover:bg-stone-900 hover:text-white sm:inline-flex" data-testid="handbook-call">
              <Phone size={13} /> 800-719-6912
            </a>
            <Link to="/#contact" className="rounded-full bg-[#82778A] px-4 py-1.5 text-xs font-semibold text-white transition-transform hover:scale-95" data-testid="handbook-quiz-cta">
              Contact us
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 md:px-8">
        {/* Hero */}
        <section className="relative mt-8 overflow-hidden rounded-3xl" data-testid="handbook-hero">
          <img src={IMAGES.hero} alt="An elderly couple sharing a warm moment at home" className="absolute inset-0 h-full w-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C2A29]/85 via-[#2C2A29]/55 to-transparent" />
          <div className="relative flex min-h-[380px] flex-col justify-center px-8 py-16 md:px-14">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/70">Silver Home Care · Client Handbook</p>
            <h1 className="mt-3 max-w-xl font-heading text-5xl leading-none tracking-tight text-white md:text-6xl">A guide to your care</h1>
            <p className="mt-4 max-w-lg text-lg leading-relaxed text-white/85">
              Everything about how we work — your rights, your care plan, your caregivers, and how to reach us at any hour. Written for you and the people who love you.
            </p>
          </div>
        </section>

        {/* Welcome letter */}
        <section className="mt-16 grid grid-cols-1 items-start gap-10 md:grid-cols-12" data-testid="handbook-welcome">
          <div className="md:col-span-4">
            <div className="rounded-2xl border border-stone-200 bg-white p-8">
              <Monogram initials="KS" />
              <p className="mt-4 font-heading text-xl font-semibold text-[#4A424E]">Kelly Schmunk</p>
              <p className="text-sm text-[#5C5856]">Administrator, Silver Home Care</p>
              <a href="tel:2675470310" className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#82778A] hover:underline">
                <Phone size={14} /> 267-547-0310
              </a>
            </div>
          </div>
          <div className="md:col-span-8">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#82778A]">A note before you begin</p>
            <h2 className="mt-2 font-heading text-3xl tracking-tight text-[#4A424E] md:text-4xl">Welcome to the family</h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-[#5C5856] md:text-lg">
              <p>Choosing home care is an act of trust — in us, and in yourself. This handbook is our promise in writing: what we do, how we do it, and the rights you keep every single day we're in your home.</p>
              <p>Read it cover to cover, or jump to what you need. And if anything is ever unclear, call me directly. My number is in this book, and I answer it.</p>
            </div>
            <p className="mt-6 font-heading text-3xl italic text-[#82778A]">Kelly</p>
          </div>
        </section>

        {/* Team */}
        <section className="mt-20" data-testid="handbook-team">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#82778A]">The people behind your care</p>
          <h2 className="mt-2 font-heading text-3xl tracking-tight text-[#4A424E] md:text-4xl">Meet your office team</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {TEAM.map((t) => (
              <div key={t.key} className="rounded-2xl border border-stone-200 bg-white p-8 transition-transform duration-300 hover:-translate-y-1" data-testid={`team-card-${t.key}`}>
                <Monogram initials={t.initials} />
                <p className="mt-4 font-heading text-xl font-semibold text-[#4A424E]">{t.name}</p>
                <p className="text-sm text-[#5C5856]">{t.role}</p>
                {t.phone && (
                  <a href={`tel:${t.phone.replace(/\D/g, "")}`} className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#82778A] hover:underline">
                    <Phone size={14} /> {t.phone}
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Handbook body */}
        <section className="mt-24" ref={articleRef}>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#82778A]">The handbook</p>
          <h2 className="mt-2 font-heading text-3xl tracking-tight text-[#4A424E] md:text-4xl">Everything, in plain language</h2>
          {data && (
            <p className="mt-2 text-sm text-[#A8A3A0]" data-testid="handbook-version">Version {data.version} · Updated {String(data.updated_at || "").slice(0, 10)}</p>
          )}

          {missing && (
            <div className="mt-10 rounded-2xl border border-dashed border-stone-300 bg-white p-16 text-center" data-testid="handbook-missing">
              <p className="font-heading text-xl font-semibold text-[#4A424E]">Handbook not available right now</p>
              <p className="mt-2 text-[#5C5856]">Please call us at 800-719-6912 and we'll send you a copy.</p>
            </div>
          )}
          {!data && !missing && (
            <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-[#82778A] border-t-transparent" /></div>
          )}

          {sections.length > 0 && (
            <div className="mt-12 grid grid-cols-12 items-start gap-12">
              <nav aria-label="Handbook sections" className="sticky top-28 col-span-3 hidden max-h-[70vh] overflow-y-auto pr-2 md:block no-print" data-testid="handbook-toc">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#A8A3A0]">Contents</p>
                <ul className="space-y-1">
                  {sections.map((s, i) => (
                    <li key={s.id}>
                      <a href={`#${s.id}`}
                        className={`block rounded-lg px-3 py-1.5 text-sm transition-all duration-200 hover:translate-x-1 ${active === s.id ? "bg-[#E2DFE6] font-semibold text-[#4A424E]" : "text-[#5C5856] hover:text-[#2C2A29]"}`}
                        data-testid={`handbook-toc-${i}`}>
                        <span className="mr-2 text-xs text-[#A8A3A0]">{String(i + 1).padStart(2, "0")}</span>{s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              <article className="col-span-12 md:col-span-9 lg:col-span-8 lg:col-start-5">
                {sections.map((s, i) => (
                  <div key={s.id}>
                    <section id={s.id} className="mb-14 scroll-mt-28" data-testid={`handbook-section-${i}`}>
                      <div className="flex flex-wrap items-baseline gap-4">
                        <span className="font-heading text-4xl font-semibold text-[#E2DFE6]">{String(i + 1).padStart(2, "0")}</span>
                        <h2 className="font-heading text-3xl tracking-tight text-[#4A424E]">{s.title}</h2>
                        <ListenButton text={`${s.title}. ${s.body}`} label="Listen" testid={`handbook-listen-${i}`} />
                      </div>
                      <div className="mt-5 border-l-2 border-[#E2DFE6] pl-6 flow-root">
                        {(() => { const secImg = SECTION_IMAGES[s.title] || SECTION_FALLBACK[i % SECTION_FALLBACK.length]; return (
                          <img src={secImg} alt="" aria-hidden="true" loading="lazy"
                            className={`mb-4 w-full rounded-2xl border border-stone-200 object-cover shadow-sm sm:w-2/5 ${i % 2 ? "sm:float-left sm:mr-6" : "sm:float-right sm:ml-6"}`}
                            data-testid={`handbook-section-img-${i}`} />
                        ); })()}
                        {renderBody(s.body, `handbook-section-${i}`)}
                      </div>
                    </section>
                    {IMAGE_BREAKS[i] && (
                      <img src={IMAGE_BREAKS[i]} alt="" aria-hidden="true" loading="lazy"
                        className="mb-14 h-64 w-full rounded-3xl object-cover md:h-80" data-testid={`handbook-image-${i}`} />
                    )}
                  </div>
                ))}
              </article>
            </div>
          )}
        </section>

        {/* Contact card */}
        <section className="mt-8 rounded-3xl bg-[#E2DFE6] p-10 md:p-14" data-testid="handbook-contact-card">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#82778A]">We're here, day and night</p>
              <h2 className="mt-2 font-heading text-3xl tracking-tight text-[#4A424E]">Talk to a real person</h2>
              <p className="mt-4 max-w-md text-base leading-relaxed text-[#5C5856]">
                A supervisor is on call 24 hours a day. For emergencies, always call 911 first — then call us if the emergency affects your care or schedule.
              </p>
            </div>
            <div className="space-y-3 text-base text-[#2C2A29]">
              <p className="flex items-center gap-3"><Phone size={18} className="text-[#82778A]" /> <span><strong>24-hour line:</strong> <a href="tel:8007196912" className="font-semibold text-[#82778A] hover:underline">800-719-6912</a> (press 2 for on-call)</span></p>
              <p className="flex items-center gap-3"><Heart size={18} className="text-[#82778A]" /> <span><strong>Kelly, Administrator:</strong> <a href="tel:2675470310" className="font-semibold text-[#82778A] hover:underline">267-547-0310</a></span></p>
              <p className="flex items-center gap-3"><Sparkle size={18} className="text-[#82778A]" /> <span><strong>Patti, Staffing:</strong> <a href="tel:9545202843" className="font-semibold text-[#82778A] hover:underline">954-520-2843</a></span></p>
              <p className="flex items-center gap-3"><ShieldCheck size={18} className="text-[#82778A]" /> <span>680 Lincoln Hwy, Fairless Hills, PA 19030 · support@silverhomecare.com</span></p>
            </div>
          </div>
        </section>

        {/* CTA footer */}
        <footer className="my-16 overflow-hidden rounded-3xl bg-[#4A424E] no-print" data-testid="handbook-cta">
          <div className="flex flex-col items-start justify-between gap-6 p-10 md:flex-row md:items-center md:p-14">
            <div>
              <h2 className="font-heading text-3xl tracking-tight text-white md:text-4xl">Not a client yet?</h2>
              <p className="mt-2 max-w-md text-base text-white/75">You may be eligible for free in-home services through Community HealthChoices. Give us a call, or fill out our form — ask us how to apply.</p>
            </div>
            <Link to="/#contact" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#4A424E] transition-transform hover:scale-95" data-testid="handbook-cta-button">
              Get in touch <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
